import { useEffect, useRef, useState } from 'react';
import { SensitiveStudentsDashboardService } from '../services/SensitiveStudentsDashboardService';
import type {
  SensitiveStudentsDashboard,
  SensitiveStudentsDashboardQuery,
} from '../utils/sensitiveStudentsDashboard';

const SEARCH_DEBOUNCE_MS = 350;

export function useSensitiveStudentsDashboard(query: SensitiveStudentsDashboardQuery) {
  const [snapshot, setSnapshot] = useState<SensitiveStudentsDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const prevSearch = useRef<string | null>(null);
  const requestId = useRef(0);

  useEffect(() => {
    const onlySearchChanged = prevSearch.current !== null
      && prevSearch.current !== query.searchName;
    prevSearch.current = query.searchName;

    const run = () => {
      const currentRequest = ++requestId.current;
      setLoading(true);
      setError(null);

      SensitiveStudentsDashboardService.getDashboard(query)
        .then((next) => {
          if (currentRequest !== requestId.current) {
            return;
          }
          setSnapshot(next);
        })
        .catch((caught: unknown) => {
          if (currentRequest !== requestId.current) {
            return;
          }
          setSnapshot(null);
          setError(caught instanceof Error ? caught.message : 'Não foi possível carregar as alunas sensíveis.');
        })
        .finally(() => {
          if (currentRequest === requestId.current) {
            setLoading(false);
          }
        });
    };

    clearTimeout(debounceTimer.current);
    if (onlySearchChanged) {
      debounceTimer.current = setTimeout(run, SEARCH_DEBOUNCE_MS);
    } else {
      run();
    }

    return () => clearTimeout(debounceTimer.current);
  }, [query]);

  return { snapshot, loading, error };
}
